import type { PaymentStage } from '@krp/sale-ui'

import type { AqsiSaleVm } from '../../sale/model/types'
import type { RmkVm } from './types'

/**
 * Этап окна РМК (UI-RMK-FLOW 3.1, RP-FB22) — одно окно на адресе источника:
 * - `loading` — моя работа ещё не прочитана (или отказ чтения нашей базы);
 * - `start` — «Начало работы»: я не работаю ни за одной кассой этого источника; кассир → код → вид цены → касса;
 * - `opening` — «Открыть смену» нажато: проверка связи, команда кассе, ожидание её ответа;
 * - `other-tab` — рабочее место закреплено за другой вкладкой (RP-CA5): здесь ничего не отправляется, пока кассир не введёт код;
 * - `shift` — порог смены: смена закрыта, открыта вне программы или идёт команда закрытия;
 * - `sale` — экран продажи.
 *
 * Своя работа решается ТОЛЬКО нашей базой (`sessions/mine`): кабинет не ответил на список касс — идущая продажа и порог
 * смены остаются на экране.
 */
export type RmkStage = 'loading' | 'start' | 'opening' | 'other-tab' | 'shift' | 'sale'

/** Этапы окна оплаты, где деньги или чек в пути (RP-PAY-ONE). */
const MONEY_IN_FLIGHT: ReadonlySet<PaymentStage> = new Set(['saving', 'preparing', 'terminal', 'checking', 'receipt', 'no-receipt', 'manual'])

/**
 * После команды закрытия продажа больше недоступна. Подтверждённое закрытие
 * имеет приоритет даже над старым состоянием оплаты, оставшимся в клиенте.
 */
export function rmkStageOf(rmk: RmkVm, sale: AqsiSaleVm, passed: boolean): RmkStage {
  if (rmk.mineQuery.status !== 'ready' && rmk.mineQuery.status !== 'empty') return 'loading'
  // Ход открытия — до выбора: человек уже нажал «Открыть смену», и «Начало работы» ему больше не нужно.
  if (rmk.opening !== null) return 'opening'
  if (rmk.mine === null) return 'start'
  if (!rmk.mine.thisTab) return 'other-tab'
  if (rmk.shift?.isOpen === false || (rmk.operation?.kind === 'close' && !rmk.operation.canDismiss)) return 'shift'
  // Деньги или чек в пути — экран продажи держится, что бы ни говорила смена: иначе окно оплаты пропало бы посреди хода.
  if (sale.payment !== null && MONEY_IN_FLIGHT.has(sale.payment.stage)) return 'sale'
  if (rmk.shiftCommands.canAdopt) return 'shift'
  if (passed) return 'sale'
  // Свою работу уже прочитали из нашей базы: корзину можно набирать, пока кабинет подтверждает смену.
  // Денежные действия отдельно закрыты shiftBlock; открытие/закрытие в пути по-прежнему держит порог.
  const commandInFlight = rmk.operation !== null && !rmk.operation.canDismiss
  return commandInFlight ? 'shift' : 'sale'
}
